import { DataForSEOBlock, DataForSEOBlockMeta } from '@/blocks/blocks/dataforseo'
import { DingTalkBlock, DingTalkBlockMeta } from '@/blocks/blocks/dingtalk'
import { FeishuBlock, FeishuBlockMeta } from '@/blocks/blocks/feishu'
import { SerpApiBlock, SerpApiBlockMeta } from '@/blocks/blocks/serpapi'
import { WeComBlock, WeComBlockMeta } from '@/blocks/blocks/wecom'
/**
 * The blocks the customer bundle always carries: the executable types the
 * Rust executor runs, the six providers a brand can connect, and the
 * triggers. `registry-maps.ts` seeds the live registry from here.
 */

import { AgentBlock } from '@/blocks/blocks/agent'
import { ApiBlock } from '@/blocks/blocks/api'
import { ApiTriggerBlock } from '@/blocks/blocks/api_trigger'
import { ChatTriggerBlock } from '@/blocks/blocks/chat_trigger'
import { ConditionBlock } from '@/blocks/blocks/condition'
import { EvaluatorBlock } from '@/blocks/blocks/evaluator'
import { FunctionBlock } from '@/blocks/blocks/function'
import { GenericWebhookBlock } from '@/blocks/blocks/generic_webhook'
import { GmailBlock, GmailBlockMeta, GmailV2Block, GmailV2BlockMeta } from '@/blocks/blocks/gmail'
import { GoogleDriveBlock, GoogleDriveBlockMeta } from '@/blocks/blocks/google_drive'
import { HubSpotBlock, HubSpotBlockMeta } from '@/blocks/blocks/hubspot'
import { HumanInTheLoopBlock } from '@/blocks/blocks/human_in_the_loop'
import { JiraBlock, JiraBlockMeta } from '@/blocks/blocks/jira'
import { KnowledgeBlock } from '@/blocks/blocks/knowledge'
import { NoteBlock } from '@/blocks/blocks/note'
import {
  NotionBlock,
  NotionBlockMeta,
  NotionV2Block,
  NotionV2BlockMeta,
} from '@/blocks/blocks/notion'
import { OnditBlock } from '@/blocks/blocks/ondit'
import { OnditEventBlock } from '@/blocks/blocks/ondit_event'
import { OnditNotifyBlock } from '@/blocks/blocks/ondit_notify'
import { DistributionOperations } from '@/blocks/blocks/ondit-distribution'
import { OnditBatchOperations } from '@/blocks/blocks/ondit-batch'
import { ResponseBlock } from '@/blocks/blocks/response'
import { RouterBlock } from '@/blocks/blocks/router'
import { ScheduleBlock } from '@/blocks/blocks/schedule'
import { SlackBlock, SlackBlockMeta, SlackV2Block, SlackV2BlockMeta } from '@/blocks/blocks/slack'
import { StartTriggerBlock } from '@/blocks/blocks/start_trigger'
import type { BlockConfig, BlockMeta } from '@/blocks/types'

/** All block configs keyed by block type. The execution source of truth. */
export const BLOCKS_CORE: Record<string, BlockConfig> = {
  wecom: WeComBlock,
  dataforseo: DataForSEOBlock,
  serpapi: SerpApiBlock,
  dingtalk: DingTalkBlock,
  feishu: FeishuBlock,
  agent: AgentBlock,
  api: ApiBlock,
  api_trigger: ApiTriggerBlock,
  chat_trigger: ChatTriggerBlock,
  condition: ConditionBlock,
  evaluator: EvaluatorBlock,
  function: FunctionBlock,
  ondit: OnditBlock,
  ...OnditBatchOperations,
  ...DistributionOperations,
  ondit_event: OnditEventBlock,
  ondit_notify: OnditNotifyBlock,
  generic_webhook: GenericWebhookBlock,
  gmail: GmailBlock,
  gmail_v2: GmailV2Block,
  google_drive: GoogleDriveBlock,
  hubspot: HubSpotBlock,
  human_in_the_loop: HumanInTheLoopBlock,
  jira: JiraBlock,
  knowledge: KnowledgeBlock,
  note: NoteBlock,
  notion: NotionBlock,
  notion_v2: NotionV2Block,
  response: ResponseBlock,
  router: RouterBlock,
  schedule: ScheduleBlock,
  slack: SlackBlock,
  slack_v2: SlackV2Block,
  start_trigger: StartTriggerBlock,
}

/**
 * Block presentation/catalog metas (`{ tags, templates }`) keyed by block
 * type. Sibling to `BLOCK_REGISTRY`; pulled from the same block files so the
 * two stay in lockstep without a separate registry to maintain.
 *
 * `BlockMeta` exists only for catalog-visible integrations — every key here
 * has a corresponding entry in `packages/deployment-config/src/integrations.json`. Blocks
 * absent from the catalog (core blocks like `agent`/`api`, superseded base
 * versions, and hidden tools) carry no meta because the only consumers are
 * integration surfaces: `getTemplatesForBlock` (the two integration detail
 * pages) and `getAllBlockMeta()` → `POPULAR_WORKFLOWS` (landing integrations
 * index). The toolbar and search modal read block *configs*, not metas.
 */
export const BLOCK_META_CORE: Record<string, BlockMeta> = {
  wecom: WeComBlockMeta,
  dataforseo: DataForSEOBlockMeta,
  serpapi: SerpApiBlockMeta,
  dingtalk: DingTalkBlockMeta,
  feishu: FeishuBlockMeta,
  gmail: GmailBlockMeta,
  gmail_v2: GmailV2BlockMeta,
  google_drive: GoogleDriveBlockMeta,
  hubspot: HubSpotBlockMeta,
  jira: JiraBlockMeta,
  notion: NotionBlockMeta,
  notion_v2: NotionV2BlockMeta,
  slack: SlackBlockMeta,
  slack_v2: SlackV2BlockMeta,
}
